// Auth-flow request schemas. Email + password is the only auth at month 12.
import { z } from 'zod';

import {
  csrfTokenSchema,
  emailSchema,
  localeSchema,
  passwordSchema,
  safeNameSchema,
  timezoneSchema,
} from './common.js';

export const signUpInputSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  display_name: safeNameSchema(2, 80),
  role: z.enum(['artist', 'host']),
  locale: localeSchema.optional(),
  timezone: timezoneSchema.optional(),
  accepted_terms_version: z.string().min(1).max(20),
  accepted_covenant: z.literal(true, {
    message: 'You must agree to the WWAAVE Exchange Covenant.',
  }),
  // Verification & Safety MVP §6.3 — explicit age affirmation at sign-up. The
  // Terms already require 18+; this is the unmissable, per-account tick. Same
  // literal-true shape as `accepted_covenant` so a `false`/missing value is a
  // hard 400.
  age_affirmed: z.literal(true, {
    message: 'You must confirm you are 18 or older.',
  }),
});
export type SignUpInput = z.infer<typeof signUpInputSchema>;

export const signInInputSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200), // permissive — never leak length policy on sign-in
  remember_me: z.boolean().optional().default(false),
});
export type SignInInput = z.infer<typeof signInInputSchema>;

export const signOutInputSchema = z.object({
  csrf_token: csrfTokenSchema,
});
export type SignOutInput = z.infer<typeof signOutInputSchema>;

export const resetPasswordRequestInputSchema = z.object({
  email: emailSchema,
});
export type ResetPasswordRequestInput = z.infer<typeof resetPasswordRequestInputSchema>;

export const resetPasswordConfirmInputSchema = z.object({
  token: z.string().min(16).max(256),
  new_password: passwordSchema,
});
export type ResetPasswordConfirmInput = z.infer<typeof resetPasswordConfirmInputSchema>;

export const changePasswordInputSchema = z.object({
  current_password: z.string().min(1).max(200),
  new_password: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>;

export const verifyEmailInputSchema = z.object({
  token: z.string().min(16).max(256),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailInputSchema>;

// -----------------------------------------------------------------------------
// Wave 1 dual-role + events
// -----------------------------------------------------------------------------

/** Body for POST /api/auth/add-capability — gives a session user a missing role-shape. */
export const addCapabilityInputSchema = z.object({
  kind: z.enum(['artist', 'host']),
});
export type AddCapabilityInput = z.infer<typeof addCapabilityInputSchema>;

/** Body for POST /api/auth/active-hat — switches the wwaave_hat cookie. */
export const setActiveHatInputSchema = z.object({
  hat: z.enum(['artist', 'host', 'admin']),
});
export type SetActiveHatInput = z.infer<typeof setActiveHatInputSchema>;
